<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { getUnixStartOfDay, getUnixEndOfDay } from 'shared/helpers/DateHelper';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import subDays from 'date-fns/subDays';
import { format } from 'date-fns';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'confirm']);

const store = useStore();
const agents = useMapGetter('agents/getAgents');

const dialogRef = ref(null);
const selectedAgentId = ref(null);
const searchQuery = ref('');
const fromDate = ref(subDays(new Date(), 6));
const toDate = ref(new Date());

const fromDateString = computed({
  get: () => format(fromDate.value, 'yyyy-MM-dd'),
  set: value => {
    if (value) {
      fromDate.value = new Date(value + 'T00:00:00');
    }
  },
});

const toDateString = computed({
  get: () => format(toDate.value, 'yyyy-MM-dd'),
  set: value => {
    if (value) {
      toDate.value = new Date(value + 'T23:59:59');
    }
  },
});

const filteredAgents = computed(() => {
  if (!agents.value) return [];

  const query = searchQuery.value.toLowerCase().trim();
  if (!query) return agents.value;

  return agents.value.filter(
    agent =>
      agent.name?.toLowerCase().includes(query) ||
      agent.email?.toLowerCase().includes(query)
  );
});

const selectedAgent = computed(() => {
  if (!selectedAgentId.value) return null;
  return agents.value?.find(agent => agent.id === selectedAgentId.value);
});

const canExport = computed(() => {
  return (
    selectedAgentId.value !== null &&
    fromDate.value !== null &&
    toDate.value !== null &&
    fromDate.value <= toDate.value
  );
});

const onAgentSelect = agentId => {
  selectedAgentId.value = agentId;
};

const handleConfirm = () => {
  if (canExport.value) {
    const from = getUnixStartOfDay(fromDate.value);
    const to = getUnixEndOfDay(toDate.value);
    emit('confirm', {
      agent: selectedAgent.value,
      from,
      to,
    });
  }
};

const handleClose = () => {
  selectedAgentId.value = null;
  searchQuery.value = '';
  fromDate.value = subDays(new Date(), 6);
  toDate.value = new Date();
  emit('close');
};

onMounted(() => {
  store.dispatch('agents/get');
});

watch(
  () => props.show,
  newValue => {
    if (newValue) {
      dialogRef.value?.open();
    } else {
      dialogRef.value?.close();
    }
  }
);
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="$t('AGENT_REPORTS.DETAILED_REPORT_MODAL.TITLE')"
    :description="$t('AGENT_REPORTS.DETAILED_REPORT_MODAL.DESCRIPTION')"
    :cancel-button-label="$t('DIALOG.BUTTONS.CANCEL')"
    :confirm-button-label="$t('AGENT_REPORTS.DETAILED_REPORT_MODAL.EXPORT')"
    :disable-confirm-button="!canExport"
    @confirm="handleConfirm"
    @close="handleClose"
  >
    <div class="flex flex-col gap-4">
      <div class="grid grid-cols-2 gap-4">
        <div>
          <label class="block mb-2 text-sm font-medium text-n-slate-12">
            {{ $t('AGENT_REPORTS.DETAILED_REPORT_MODAL.FROM_DATE_LABEL') }}
          </label>
          <input
            v-model="fromDateString"
            type="date"
            class="w-full px-3 py-2 text-sm border rounded-lg border-n-strong bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-9"
          />
        </div>
        <div>
          <label class="block mb-2 text-sm font-medium text-n-slate-12">
            {{ $t('AGENT_REPORTS.DETAILED_REPORT_MODAL.TO_DATE_LABEL') }}
          </label>
          <input
            v-model="toDateString"
            type="date"
            class="w-full px-3 py-2 text-sm border rounded-lg border-n-strong bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-9"
          />
        </div>
      </div>

      <div>
        <label class="block mb-2 text-sm font-medium text-n-slate-12">
          {{ $t('AGENT_REPORTS.DETAILED_REPORT_MODAL.SELECT_AGENT_LABEL') }}
        </label>
        <div class="relative">
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="
              $t('AGENT_REPORTS.DETAILED_REPORT_MODAL.SEARCH_PLACEHOLDER')
            "
            class="w-full px-3 py-2 text-sm border rounded-lg border-n-strong bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-9"
          />
        </div>
      </div>

      <div
        class="max-h-64 overflow-y-auto border rounded-lg border-n-strong bg-n-solid-1"
      >
        <div
          v-if="filteredAgents.length === 0"
          class="px-4 py-8 text-sm text-center text-n-slate-11"
        >
          {{ $t('AGENT_REPORTS.DETAILED_REPORT_MODAL.NO_AGENTS_FOUND') }}
        </div>
        <div
          v-for="agent in filteredAgents"
          :key="agent.id"
          :class="{
            'bg-n-alpha-black2': selectedAgentId === agent.id,
            'hover:bg-n-alpha-black1': selectedAgentId !== agent.id,
          }"
          class="flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors"
          @click="onAgentSelect(agent.id)"
        >
          <Avatar :src="agent.thumbnail" :username="agent.name" size="32px" />
          <div class="flex flex-col flex-1 min-w-0">
            <span class="text-sm font-medium text-n-slate-12 truncate">
              {{ agent.name }}
            </span>
            <span class="text-xs text-n-slate-11 truncate">
              {{ agent.email }}
            </span>
          </div>
          <div
            v-if="selectedAgentId === agent.id"
            class="flex-shrink-0 text-n-blue-9"
          >
            <fluent-icon icon="checkmark-outline" size="20" />
          </div>
        </div>
      </div>
    </div>
  </Dialog>
</template>
